import { ScrollArea } from '@/components/ui/scroll-area';
import { useSidebar } from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';
import { FC, ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { NormalizedColumn, RepublicTableRow } from '../../../../models';
import { TableSkeleton } from '../../../table-skeleton/table-skeleton';
import useTable from './useTable';

interface TableRepublicProps {
  onTotalsChange?: (masters: number, apprentices: number) => void;
}

const resolveCellValue = (
  col: NormalizedColumn,
  row: RepublicTableRow,
  index: number,
): ReactNode => {
  if (col.key === '__index') {
    return <span className="text-gray-500">{index + 1}</span>;
  }

  if (col?.isDynamic && col?.actionId) {
    const match = row.rated_actions.find((ra) => ra.action_id === col.actionId);
    const count = match?.rated_actions_count ?? 0;
    return count.toLocaleString();
  }

  const value = row[col.key as keyof RepublicTableRow];
  if (typeof value === 'number') return value.toLocaleString();
  return value as ReactNode;
};

const thBaseNumber =
  'border border-gray-300 p-3 text-sm font-semibold text-gray-700 bg-[#f5f5f5] min-w-[60px]';

const thBase =
  'border border-gray-300 p-3 text-sm font-semibold text-gray-700 bg-[#f5f5f5] min-w-[120px]';

const tdBase = 'border border-gray-200 px-3 py-2 text-sm text-gray-800';

export const TableRepublic: FC<TableRepublicProps> = ({ onTotalsChange }) => {
  const { open } = useSidebar();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    columns,
    rows,
    totals,
    isLoading,
    groupLabel,
    mastersTotal,
    apprenticesTotal,
  } = useTable();

  const staticColumns = columns.filter((col) => !col.isDynamic);
  const dynamicColumns = columns.filter((col) => col.isDynamic);
  const hasData = rows.length > 0;

  useEffect(() => {
    onTotalsChange?.(mastersTotal, apprenticesTotal);
  }, [mastersTotal, apprenticesTotal]);

  return (
    <div className="w-full rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
      <ScrollArea
        className={cn(open ? 'w-[calc(100vw-320px)]' : 'w-[calc(100vw-150px)]')}
      >
        <table className="min-w-full border-collapse border border-gray-300">
          <thead>
            <tr>
              {staticColumns.map((col) => (
                <th
                  key={col.key}
                  rowSpan={2}
                  className={cn(
                    col.key === '__index' ? thBaseNumber : thBase,
                    'align-middle text-center',
                  )}
                >
                  {col.label}
                </th>
              ))}

              {dynamicColumns.length > 0 && (
                <th
                  colSpan={dynamicColumns.length}
                  className={cn(thBase, 'text-center')}
                >
                  {groupLabel ?? t('current.month')}
                </th>
              )}
            </tr>

            <tr>
              {dynamicColumns.map((col) => (
                <th
                  key={col.key}
                  className={cn(thBase, 'text-center align-middle')}
                >
                  {col.label}
                </th>
              ))}
            </tr>

            {hasData && !isLoading && (
              <tr className="bg-[#e8f0f7]">
                {columns.map((col) => {
                  const isIndexCol = col.key === '__index';
                  const isRegionCol = col.key === 'region_name';
                  const totalValue = totals[col.key];

                  return (
                    <td
                      key={col.key}
                      className={cn(
                        tdBase,
                        'font-bold text-center text-gray-900',
                      )}
                    >
                      {isIndexCol ? null : isRegionCol ? (
                        <span>{t('total')}:</span>
                      ) : col.aggregate === 'total' ? (
                        (totalValue ?? 0).toLocaleString()
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            )}
          </thead>

          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <TableSkeleton columns={columns.length} />
            ) : hasData ? (
              rows.map((row, rowIndex) => (
                <tr
                  key={row.region_id}
                  className="hover:bg-blue-50/50 transition-colors duration-150"
                >
                  {columns.map((col) => {
                    const isRegionName = col.key === 'region_name';
                    const value = resolveCellValue(col, row, rowIndex);

                    return (
                      <td
                        key={col.key}
                        className={cn(
                          tdBase,
                          col.key === '__index' && 'text-center',
                          col.isDynamic && 'text-center',
                          isRegionName &&
                            'font-semibold text-dark-blue cursor-pointer hover:underline',
                        )}
                        onClick={() =>
                          isRegionName && navigate(`${row.region_id}`)
                        }
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length}>
                  <div className="flex h-[260px] flex-col items-center justify-center gap-3 text-gray-500">
                    <svg
                      className="w-16 h-16 text-gray-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                      />
                    </svg>
                    <p className="text-sm font-medium text-gray-600">
                      {t('nothing.found')}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </ScrollArea>
    </div>
  );
};
